import { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, Home, AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Creators Archive Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/assets/logo.png" 
                alt="Creators Archive" 
                className="h-12 w-auto"
              />
            </div>

            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-red-100 rounded-full blur-xl opacity-50 animate-pulse"></div>
                <div className="relative bg-white rounded-full p-6 shadow-lg">
                  <AlertCircle className="w-16 h-16 text-red-500" strokeWidth={1.5} />
                </div>
              </div>
            </div>

            {/* Error Message */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Oops! Something went wrong
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed">
                We encountered an unexpected error. Don't worry, your work is safe. 
                Try refreshing the page to continue.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleRefresh}
                className="w-full group relative px-6 py-4 bg-gray-900 text-white rounded-2xl font-medium 
                         hover:bg-gray-800 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
                         flex items-center justify-center gap-3"
              >
                <RefreshCw 
                  size={20} 
                  className="transition-transform duration-300 group-hover:rotate-180" 
                />
                <span>Refresh Page</span>
              </button>

              <button
                onClick={this.handleGoHome}
                className="w-full px-6 py-4 border-2 border-gray-200 text-gray-700 rounded-2xl font-medium 
                         hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 hover:scale-[1.02]
                         flex items-center justify-center gap-3"
              >
                <Home size={20} />
                <span>Go to Home</span>
              </button>
            </div>

            {/* Additional Info */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center mb-3">
                If this issue persists, please try clearing your browser cache or contact us.
              </p>
              <a 
                href="mailto:info@ankaragrid.africa"
                className="text-sm text-gray-700 hover:text-gray-900 font-medium text-center block transition-colors"
              >
                info@ankaragrid.africa
              </a>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden -z-10">
              <div className="absolute top-20 left-10 w-32 h-32 bg-red-100 rounded-full blur-3xl opacity-20"></div>
              <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-20"></div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
